import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon, CheckCircleIcon } from "@chakra-ui/icons";
import {
  FaBrain,
  FaCode,
  FaHandshake,
  FaProjectDiagram,
  FaRobot,
  FaShieldAlt,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/utilities/navbar";
import Footer from "../components/utilities/footer";
import { theme } from "../components/utilities/theme";

const MotionBox = motion.create(Box);

const principles = [
  {
    icon: FaBrain,
    title: "Automation with context",
    text: "We design assistants and workflows around real business data, rules, and review points.",
  },
  {
    icon: FaCode,
    title: "Engineering depth",
    text: "We build the APIs, apps, dashboards, and cloud systems that make automation useful in production.",
  },
  {
    icon: FaShieldAlt,
    title: "Responsible delivery",
    text: "Security, access control, monitoring, and clear handoff paths are part of the product plan.",
  },
  {
    icon: FaHandshake,
    title: "Close collaboration",
    text: "We work with teams in visible steps, from fuzzy ideas to shipped, maintainable systems.",
  },
];

const deliverySteps = [
  {
    title: "Discover",
    text: "Understand your users, workflow, data, constraints, and the decision you need to make next.",
  },
  {
    title: "Prototype",
    text: "Shape the smallest useful version of the app, workflow, or automation path.",
  },
  {
    title: "Build",
    text: "Implement production-ready interfaces, APIs, integrations, automation logic, and deployment flows.",
  },
  {
    title: "Improve",
    text: "Use feedback, metrics, and operational learning to refine the product after launch.",
  },
];

const focusAreas = [
  "Assistants for support, operations, research, and internal tools.",
  "Web and mobile apps that give automation a clear user experience.",
  "Data pipelines, APIs, and dashboards that keep teams informed.",
  "Technical consultation for teams deciding what to build first.",
];

export default function About() {
  const {
    pageBg,
    sectionBg,
    cardBg,
    textColor,
    textSecondary,
    accentColor,
    accentCyan,
  } = theme.colors;

  return (
    <Box bg={pageBg} color={textColor} minH="100vh">
      <Navbar />

      <MotionBox
        id="main-content"
        as="main"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 12, md: 18 }}>
          <Stack direction={{ base: "column", lg: "row" }} spacing={10} align="center">
            <Box flex="1.1">
              <Badge
                bg="rgba(0, 255, 208, 0.1)"
                color={accentCyan}
                border="1px solid rgba(0, 255, 208, 0.28)"
                borderRadius="full"
                px={4}
                py={1}
                mb={5}
              >
                About Orvitrix
              </Badge>
              <Heading as="h1" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.08" mb={5}>
                We build useful digital products for teams that want practical momentum.
              </Heading>
              <Text color={textSecondary} fontSize={{ base: "md", md: "xl" }} lineHeight="1.8">
                Orvitrix is a product engineering team blending strategy, design,
                full-stack development, cloud systems, and smart automation. We help
                businesses move from vague possibility to usable software.
              </Text>
              <HStack spacing={3} flexWrap="wrap" mt={7}>
                <Button
                  as={RouterLink}
                  to="/contact"
                  bg={accentColor}
                  color="white"
                  rightIcon={<ArrowForwardIcon />}
                  borderRadius="lg"
                  px={7}
                  _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}
                >
                  Talk to Us
                </Button>
                <Button
                  as={RouterLink}
                  to="/services"
                  variant="outline"
                  borderColor="rgba(0, 255, 208, 0.5)"
                  color={accentCyan}
                  borderRadius="lg"
                  px={7}
                  _hover={{ bg: "rgba(0, 255, 208, 0.08)" }}
                >
                  View Services
                </Button>
              </HStack>
            </Box>

            <Box
              flex="0.9"
              bg="rgba(15, 23, 42, 0.78)"
              border="1px solid rgba(255, 255, 255, 0.12)"
              borderRadius="xl"
              p={{ base: 6, md: 7 }}
            >
              <Flex align="center" gap={4} mb={6}>
                <Flex
                  align="center"
                  justify="center"
                  w={14}
                  h={14}
                  borderRadius="lg"
                  bg="rgba(255, 111, 32, 0.14)"
                  border="1px solid rgba(255, 111, 32, 0.42)"
                >
                  <Icon as={FaRobot} color={accentColor} boxSize={7} />
                </Flex>
                <Box>
                  <Text color={textSecondary} fontSize="sm">
                    Our lane
                  </Text>
                  <Heading size="sm">Product engineering plus automation</Heading>
                </Box>
              </Flex>
              <VStack align="stretch" spacing={3}>
                {focusAreas.map((item) => (
                  <HStack key={item} align="start" spacing={3}>
                    <CheckCircleIcon color={accentCyan} mt="3px" />
                    <Text color={textSecondary}>{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Stack>
        </Container>

        <Box bg={sectionBg} py={{ base: 12, md: 16 }}>
          <Container maxW="1360px" px={{ base: 4, md: 8 }}>
            <SectionHeading
              eyebrow="How We Think"
              title="Automation is most valuable when it is connected to a workflow"
              text="We avoid one-off demos and build around the pieces that make automation dependable: data, interfaces, integrations, security, and feedback loops."
            />
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
              {principles.map((principle) => (
                <Box
                  key={principle.title}
                  bg={cardBg}
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  borderRadius="xl"
                  p={6}
                  minH="100%"
                >
                  <Icon as={principle.icon} color={accentColor} boxSize={7} mb={5} />
                  <Heading size="sm" mb={3}>
                    {principle.title}
                  </Heading>
                  <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                    {principle.text}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 12, md: 16 }}>
          <Stack direction={{ base: "column", lg: "row" }} spacing={8} align="start">
            <Box flex="0.9">
              <SectionHeading
                eyebrow="Delivery Model"
                title="From strategy to shipped systems"
                text="We keep the work visible and concrete, especially when the product involves unknowns around data, integrations, automation behavior, or user adoption."
              />
            </Box>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} flex="1.1">
              {deliverySteps.map((step, index) => (
                <Box
                  key={step.title}
                  bg={cardBg}
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  borderRadius="xl"
                  p={6}
                >
                  <Badge bg="rgba(0, 255, 208, 0.1)" color={accentCyan} borderRadius="full" mb={4}>
                    {String(index + 1).padStart(2, "0")}
                  </Badge>
                  <Heading size="sm" mb={3}>
                    {step.title}
                  </Heading>
                  <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                    {step.text}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Stack>
        </Container>

        <Box bg="rgba(15, 23, 42, 0.72)" py={{ base: 12, md: 16 }}>
          <Container maxW="900px" px={{ base: 4, md: 8 }} textAlign="center">
            <Icon as={FaProjectDiagram} color={accentCyan} boxSize={10} mb={4} />
            <Heading size="xl" mb={4}>
              We are useful when the problem is part product, part engineering, part operations.
            </Heading>
            <Text color={textSecondary} fontSize="lg" lineHeight="1.8" mb={7}>
              Bring us a workflow, product idea, or system bottleneck. We will help
              identify the right technical path and build the parts that matter.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={accentColor}
              color="white"
              rightIcon={<ArrowForwardIcon />}
              borderRadius="lg"
              px={7}
              _hover={{ bg: "#ff853d" }}
            >
              Start a Conversation
            </Button>
          </Container>
        </Box>
      </MotionBox>

      <Footer />
    </Box>
  );
}

function SectionHeading({ eyebrow, title, text }) {
  const { accentCyan, textSecondary } = theme.colors;

  return (
    <Box mb={8}>
      <Text color={accentCyan} fontWeight="bold" mb={2}>
        {eyebrow}
      </Text>
      <Heading size={{ base: "lg", md: "xl" }} mb={3}>
        {title}
      </Heading>
      <Text color={textSecondary} maxW="760px" lineHeight="1.8">
        {text}
      </Text>
    </Box>
  );
}
